import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Drawer,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Chip } from "@mui/material";
import { formatTo12Hour } from "../utils/time";
import TruncatedTitle from "./TruncatedTitle";

interface ClassPurchaseDrawerProps {
  open: boolean;
  onClose: () => void;
  classData: any | null;
  isJoined?: boolean;
  onBookAction: () => void;
  requiresPass: boolean;
}

const ClassPurchaseDrawer: React.FC<ClassPurchaseDrawerProps> = ({
  open,
  onClose,
  classData,
  isJoined = false,
  onBookAction,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (open) {
      setIsExpanded(false);
    }
  }, [open, classData?.id]);

  if (!classData) return null;

  const dateStr = classData.singleDay || classData.day || "";
  let formattedDate = "";
  let isPast = false;
  if (dateStr) {
    const classDate = new Date(`${dateStr}T00:00:00`);
    const weekday = classDate.toLocaleDateString("en-US", { weekday: "short" });
    const day = classDate.getDate();
    const month = classDate.toLocaleDateString("en-US", { month: "short" });
    formattedDate = `${weekday} ${day} ${month}`;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    isPast = classDate < today;
  }

  const timeRange = `${formatTo12Hour(classData.startTime)} - ${formatTo12Hour(
    classData.endTime,
  )}`;

  const classImageSrc = classData.coverImage
    ? classData.coverImage.startsWith("data:")
      ? classData.coverImage
      : classData.coverImage.startsWith("http")
        ? classData.coverImage
        : `data:image/*;base64,${classData.coverImage}`
    : "";

  const description =
    classData.description || "No description provided for this class.";
  const words = description.split(/\s+/);
  const isLongDescription = words.length > 80;
  const displayedDescription =
    isLongDescription && !isExpanded
      ? `${words.slice(0, 80).join(" ")}...`
      : description;

  const actionLabel = isJoined ? "Already Joined" : isPast ? "Closed" : "Book Class Now";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 440 },
          background: "linear-gradient(180deg, #FFFFFF 0%, #FCFDFB 52%, #F5F8F3 100%)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderBottom: "1px solid #EDF2EA",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
            Class Details
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.25 }}>
            Review this class and continue with your booking.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ border: "1px solid #E5E7EB", borderRadius: "10px", color: "#6B7280" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, sm: 3 },
          pt: 1,
          pb: 2.5,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack spacing={2.25}>
          <Box
            sx={{
              borderRadius: "24px",
              overflow: "hidden",
              background: "linear-gradient(135deg, #0F172A 0%, #1F7A3F 48%, #A4D65E 100%)",
              color: "#fff",
              position: "relative",
            }}
          >
            <Box sx={{ width: "100%", height: 240, position: "relative" }}>
              {classImageSrc ? (
                <Box
                  component="img"
                  src={classImageSrc}
                  alt={classData.className}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: "#E5E7EB",
                  }}
                />
              )}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(15,23,42,0.1) 0%, rgba(15,23,42,0.78) 100%)",
                }}
              />
            </Box>

            <Box sx={{ position: "absolute", top: 18, left: 18, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {classData.classType && (
                <Chip
                  label={String(classData.classType).replace(/_/g, " ")}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.14)",
                    color: "#fff",
                    fontWeight: 800,
                    border: "1px solid rgba(255,255,255,0.18)",
                  }}
                />
              )}
              {isJoined && (
                <Chip
                  label="Joined"
                  size="small"
                  sx={{
                    backgroundColor: "#FFFFFF",
                    color: "#2E7D32",
                    fontWeight: 800,
                  }}
                />
              )}
            </Box>

            <Box sx={{ position: "absolute", left: 20, right: 20, bottom: 20 }}>
              <TruncatedTitle
                text={classData.className}
                sx={{
                  fontSize: 28,
                  fontWeight: 900,
                  lineHeight: 1.05,
                  color: "#fff",
                }}
              />
              <Typography sx={{ mt: 0.75, fontSize: 14, fontWeight: 600, opacity: 0.95 }}>
                Instructor: {classData.instructorName}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: "20px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E8EEE3",
              boxShadow: "0px 12px 30px rgba(17, 24, 39, 0.06)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 2.5, py: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#6B7280", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Class details
              </Typography>
              <Stack spacing={1.25} sx={{ mt: 1.75 }}>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Date</Typography>
                  <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700 }}>
                    {formattedDate}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Time</Typography>
                  <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700 }}>
                    {timeRange}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: "20px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E8EEE3",
              boxShadow: "0px 12px 30px rgba(17, 24, 39, 0.06)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 2.5, py: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#6B7280", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Description
              </Typography>
              <Typography
                sx={{
                  mt: 1.5,
                  color: "#64748B",
                  lineHeight: 1.75,
                  whiteSpace: "pre-wrap",
                  fontSize: "0.95rem",
                }}
              >
                {displayedDescription}
              </Typography>
              {isLongDescription && (
                <Typography
                  component="span"
                  onClick={() => setIsExpanded(!isExpanded)}
                  sx={{
                    color: "#92D026",
                    cursor: "pointer",
                    fontWeight: 800,
                    mt: 1.25,
                    display: "inline-block",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {isExpanded ? "Read Less" : "Read More"}
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: "1px solid #E8EEE3",
          backgroundColor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Button
          fullWidth
          variant="contained"
          disabled={isJoined || isPast}
          startIcon={isJoined ? <CheckCircleOutlineIcon /> : isPast ? null : <CalendarTodayOutlinedIcon />}
          onClick={() => {
            if (!isJoined && !isPast) {
              onBookAction();
              onClose();
            }
          }}
          sx={{
            backgroundColor: isPast && !isJoined ? "#FFEBEE" : isJoined ? "#E8F5E9" : "#6BC242",
            color: isPast && !isJoined ? "#D32F2F" : isJoined ? "#2E7D32" : "#fff",
            borderRadius: "14px",
            textTransform: "none",
            fontSize: 15,
            py: 1.6,
            minHeight: 56,
            fontWeight: 800,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: isPast && !isJoined ? "#FFEBEE" : isJoined ? "#E8F5E9" : "#62B63C",
            },
            "&:disabled": {
              backgroundColor: isPast && !isJoined ? "#FFEBEE" : "#E8F5E9",
              color: isPast && !isJoined ? "#D32F2F" : "#2E7D32",
            },
          }}
        >
          {actionLabel}
        </Button>
      </Box>
    </Drawer>
  );
};

export default ClassPurchaseDrawer;
